import React, { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import translate from '@translate';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import TopBlock from './TopBlock';
import { getOrdersGridFetch } from 'src/resources/ordersGrid/reducer';
import { RootStoreType } from 'src/types/store';
import {
    allColumns,
    allColumnsWithProvider,
    CREATE_CLAIM_STATUSES,
    CREATE_RETURN_STATUSES,
    extendProductColumns,
    extendProductSummarizedColumns,
    REQUIRED_ORDERS_GRID_ALL_PROVIDERS_COLUMNS,
    REQUIRED_ORDERS_GRID_COLUMNS
} from './constants';
import Table from 'src/components/Table';
import {
    ExportTypeType,
    GridFilterType,
    OnChangeOptionsType,
    RowActionPropsType,
    SelectRowType,
    TableColumnType,
    TablePropsOptionsType,
    TableRefType,
    TableRowType
} from 'src/types/table';
import { orderTypeCodes } from 'src/resources/constantsCodes/constants';
import { ClaimIcon, OrderIcon, ReturnIcon } from 'src/components/SkladonIcon/Icons';
import { useDebounce } from 'src/utils/debounce';
import { countryOptionsCodes } from 'src/utils/countryOptions';
import filterUnique from 'src/utils/filterUnique';
import { editUserSettingsFetch } from 'src/resources/userSettings/reducer';
import { UserSettingsType } from 'src/types/userSettings';
import { OrderProductType, OrderType } from 'src/types/order';
import checkOrChangeProvider from 'src/utils/checkOrChangeProvider';
import { useCheckPermission } from 'src/utils/checkPermission';
import { dowlonadCn22Zip, getOrderClaims, getOrderReturns } from './functions';
import { canViewEgon, getOrderGridColumns, sanitizeEgonGridOptions } from './egonColumns';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import { buildClaimExistsDialogParams } from '../ClaimDetailContainer/claimExistsDialog';
import {
    addOrderProductsFetch,
    patchMultipleOrdersFetch,
    removeOrderProductsFetch,
    reorderMultipleOrdersShippingFetch,
    synchronizeOrdersDCIXFetch
} from 'src/resources/ordersUpdate/reducer';
import { ConstantsCodesObjectType, ConstantsCodeType } from 'src/types/constantsCodes';
import {
    ORDER_GRID_ALL_PROVIDERS_URL,
    ORDER_GRID_PRODUCTS_SUMMARIZED_URL,
    ORDER_GRID_PRODUCTS_URL,
    ORDER_GRID_URL
} from 'src/resources/ordersGrid/sagas';
import { getStatusIcon, renderTrackingNumber } from '../OrderDetailContainer/functions';
import { disabbleMessages } from 'src/resources/alertMessages/reducer';
import colorOptionFce from 'src/utils/colorOptionFce';
import { useNavigate } from 'react-router-dom';
import AddRemoveProductDialog from './AddRemoveProductDialog';
import ChangeStatusDialog, { ChangeStatusDialogStatePropsType } from './ChangeStatusDialog';
import StatusLabel from 'src/components/StatusLabel';
import { buildShipperServiceOptions, mapShipperServiceFilters } from 'src/utils/shipperServiceFilter';

const initTableOptions: TablePropsOptionsType = {
    filters: [],
    sort: { column: 'ID', direction: 'DESC' },
    page: 1,
    items: 20,
    columns: []
};

export const SESSION_NAME = 'ordersTable';

const customRender = (
    column: TableColumnType,
    row: TableRowType,
    orderStatusCodesObject: ConstantsCodesObjectType
): ReactNode | string => {
    switch (column.key) {
        case 'STATUS':
            return (
                <StatusLabel
                    icon={getStatusIcon(row.STATUS)}
                    option={orderStatusCodesObject[row.STATUS]}
                />
            );
        case 'TRACKING_NUMBER':
            return renderTrackingNumber(row, 'TRACKING_NUMBER');
        case 'RETURN_TRACKING_NUMBER':
            return renderTrackingNumber(row, 'RETURN_TRACKING_NUMBER');
        case 'EXCHANGE_TRACKING_NUMBER':
            return renderTrackingNumber(row, 'EXCHANGE_TRACKING_NUMBER');
        default:
            return row[column.key];
    }
};

export interface AllOrdersContainerPropsType {
    filter?: 'b2c' | 'b2b' | 'cod' | 'transfer';
}

export const CANCEL_REASON_ORDER_STATES = ['inProcess', 'waitingForCarrier', 'packed'];
export type ExportDataType = 'orders' | 'ordersProducts' | 'ordersProductsSummarized';
const AllOrdersContainer: FC<AllOrdersContainerPropsType> = (props) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const tableRef = useRef<TableRefType>(null);

    const sessionOptions = JSON.parse(sessionStorage.getItem(SESSION_NAME) || 'null');

    const [propsFilter, setPropsFilter] = useState<GridFilterType[] | undefined>(undefined);
    const [allProviders, setAllProviders] = useState<boolean>(false);
    const [formatedData, setFormatedData] = useState<OrderType[]>([]);
    const [tableOptions, setTableOptions] = useState<TablePropsOptionsType>(sessionOptions || initTableOptions);
    const [selectedRows, setSelectedRows] = useState<SelectRowType[]>([]);
    const [customLoading, setCustomLoading] = useState<boolean>(false);
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);
    const [showAddRemoveProductDialog, setShowAddProductDialog] = useState<boolean>(false);
    const [changeStatusConfirmParams, setChangeStatusConfirmParams] = useState<ChangeStatusDialogStatePropsType | null>(null);

    const loading = useSelector((state: RootStoreType) => state.ordersGrid.loading);
    const ordersData = useSelector((state: RootStoreType) => state.ordersGrid.data);

    const skladonUser = useSelector((state: RootStoreType) => state.user.data?.SKLADON_USER);
    const permissionEgonView = useCheckPermission('EGON.VIEW');
    const canSeeEgon = canViewEgon(skladonUser, permissionEgonView);
    const shippingBackendCodes: ConstantsCodeType[] = [
        { value: 'egon', label: translate('orders.shippingBackend.egon') },
        { value: 'skladon', label: translate('orders.shippingBackend.skladon') }
    ];

    const orderStatusCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.orderStatusCodes);
    const orderMarketplacesCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.orderMarketplacesCodes);
    const orderStatusCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.orderStatusCodes);
    const serviceCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.serviceCodes);
    const paymentCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.paymentCodes);
    const shipperCodes = useSelector((state: RootStoreType) =>
        [...state.constantsCodes.data.shipperCodes].sort((a, b) => a.label.localeCompare(b.label))
    );
    const orderCancelReasonCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.orderCancelReasonCodes);
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);
    const currencyCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.currencyCodes);
    const userSettingsData = useSelector((state: RootStoreType) => state.userSettings.data);
    const providers = useSelector((state: RootStoreType) => state.user.data?.providers);
    const provider = useSelector((state: RootStoreType) => state.provider.data);
    const loadingMultiple = useSelector((state: RootStoreType) => state.ordersUpdate.loadingMultiple);

    const permissionAllProviders = useCheckPermission('ORDER.LISTALLPROVIDERS');
    const permissionOrderCreate = useCheckPermission('ORDER.CREATE');
    const permissionOrderDetail = useCheckPermission('ORDER.DETAIL');
    const permissionOrderUpdate = useCheckPermission('ORDER.UPDATE');
    const permissionReturnCreate = useCheckPermission('RETURN.CREATE');
    const permissionClaimCreate = useCheckPermission('CLAIM.CREATE');
    const permissionCreateMultipleClaims = useCheckPermission('CLAIM.CREATE_MULTIPLE');
    const permissionShippingCreate = useCheckPermission('ORDER.ACTION.SHIPPING.CREATE');
    const permissionDCIXImport = useCheckPermission('ORDER.ACTION.DCIX.IMPORT');
    useEffect(() => {
        let newPropsFilter: GridFilterType[] | undefined = undefined;
        switch (props.filter) {
            case 'b2c':
                newPropsFilter = [{ column: 'ORDER_TYPE', operator: 'eq', value: 'b2c' }];
                break;
            case 'b2b':
                newPropsFilter = [{ column: 'ORDER_TYPE', operator: 'eq', value: 'b2b' }];
                break;
            case 'transfer':
                newPropsFilter = [{ column: 'ORDER_TYPE', operator: 'eq', value: 'transfer' }];
                break;
            case 'cod':
                newPropsFilter = [{ column: 'PAYMENT_METHOD', operator: 'eq', value: '0' }];
                break;
        }
        setPropsFilter(newPropsFilter);
    }, [props.filter]);

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('orders.title') }));
    }, []);

    const debouncedTableOptions = useDebounce(tableOptions, 500);

    useEffect(() => {
        // props filter passed but not set in state
        if (props.filter && !propsFilter) {
            return;
        }
        loadTableData();
    }, [debouncedTableOptions, propsFilter]);

    useEffect(() => {
        if (ordersData?.data) {
            setFormatedData(getFormatedData(ordersData?.data || []));
        }
    }, [ordersData?.data]);

    const getFormatedRow = (row: TableRowType): TableRowType => {
        const provider = providers?.find((provider) => provider.ESHOP_KEY === row.ESHOP_KEY);
        return { ...row, providerName: provider?.COMPANY_NAME };
    };

    const getFormatedData = (data: TableRowType[]): TableRowType[] => {
        if (!allProviders) {
            return data;
        }
        return data.map((item) => {
            return getFormatedRow(item);
        });
    };

    const getAllGridParams = () => {
        const safeOptions = sanitizeEgonGridOptions(tableOptions, canSeeEgon);
        return {
            filters: [
                mapShipperServiceFilters(
                    [...(propsFilter || []), ...(safeOptions.filters || [])],
                    'SHIPPER_SERVICE_CODE',
                    'SHIPPER_SERVICE',
                    serviceCodes
                )
            ],
            sorts: [tableOptions.sort],
            page: tableOptions.page,
            items: tableOptions.items,
            columns: [
                ...(allProviders ? REQUIRED_ORDERS_GRID_ALL_PROVIDERS_COLUMNS : REQUIRED_ORDERS_GRID_COLUMNS),
                ...safeOptions.columns.filter((column) => !column.allwaysShow).map((column) => column.key)
            ].filter(filterUnique)
        };
    };

    const loadTableData = () => {
        if (tableOptions.columns.length) {
            dispatch(getOrdersGridFetch({ params: getAllGridParams(), allProviders }));
        }
    };

    const reloadTableData = () => {
        dispatch(getOrdersGridFetch(undefined));
    };

    const handleChangeOptions: OnChangeOptionsType = (newOptions) => {
        setTableOptions(newOptions);
    };

    const handleExport = (type: ExportTypeType, dataType: ExportDataType) => {
        let fileName = 'orders.action.exportFileName';
        if (dataType === 'ordersProducts') {
            fileName = 'orders.action.exportWithProductsFileName';
        } else if (dataType === 'ordersProductsSummarized') {
            fileName = 'orders.action.exportWithSummarizedProductsFileName';
        } else if (props.filter === 'cod') {
            fileName = 'orders.action.exportFileNameCOD';
        }

        let url = ORDER_GRID_URL;
        if (allProviders) {
            url = ORDER_GRID_ALL_PROVIDERS_URL;
        } else if (dataType === 'ordersProducts') {
            url = ORDER_GRID_PRODUCTS_URL;
        } else if (dataType === 'ordersProductsSummarized') {
            url = ORDER_GRID_PRODUCTS_SUMMARIZED_URL;
        }

        let extendColumns: TableColumnType[] | undefined = undefined;
        if (dataType === 'ordersProducts') {
            extendColumns = extendProductColumns;
        } else if (dataType === 'ordersProductsSummarized') {
            extendColumns = extendProductSummarizedColumns;
        }

        tableRef.current?.exportTableGrid(
            type,
            translate(fileName),
            getAllGridParams,
            url,
            extendColumns,
            { rowFunction: allProviders ? getFormatedRow : undefined }
        );
    };

    const editUserSettings = (newUserSettings: UserSettingsType) => {
        dispatch(editUserSettingsFetch(newUserSettings));
    };

    const changeProvider = (row: TableRowType) => {
        if (allProviders && providers && provider) {
            if (checkOrChangeProvider(providers, provider, 'ESHOP_KEY', row.ESHOP_KEY)) {
                dispatch(disabbleMessages());
            }
        }
    };

    const handleDowlonadCn22 = async () => {
        setCustomLoading(true);
        await dowlonadCn22Zip(selectedRows);
        setCustomLoading(false);
    };

    const handleReorderShipping = () => {
        setConfirmParams({
            title: translate('orders.action.reorderShipping'),
            content: translate('orders.action.reorderShippingText', { count: selectedRows.length }),
            action: () => {
                dispatch(
                    reorderMultipleOrdersShippingFetch(
                        ordersData?.data.filter((order) => selectedRows.includes(String(order.ID)))
                    )
                );
            }
        });
    };

    const handleSynchronizeDCIX = () => {
        setConfirmParams({
            title: translate('orders.action.synchronizeDcix'),
            content: translate('orders.action.synchronizeDcixText', { count: selectedRows.length }),
            action: () => {
                dispatch(
                    synchronizeOrdersDCIXFetch(
                        ordersData?.data.filter((order) => selectedRows.includes(String(order.ID)))
                    )
                );
            }
        });
    };

    const handleMassPrintFiles = () => {
        setConfirmParams({
            title: translate('orders.action.printFiles'),
            content: translate('orders.action.printFilesText', { count: selectedRows.length }),
            options: boolNumberCodes,
            optionsValue: 1,
            action: (value: ExportTypeType) => {
                dispatch(
                    patchMultipleOrdersFetch({
                        orders: ordersData?.data.filter((order) => selectedRows.includes(String(order.ID))),
                        data: { PRINT_FILES: value }
                    })
                );
            }
        });
    };

    const handleMassAllowExpeditions = () => {
        setConfirmParams({
            title: translate('orders.action.allowExpeditions'),
            content: translate('orders.action.allowExpeditionsText', { count: selectedRows.length }),
            options: boolNumberCodes,
            optionsValue: 1,
            action: (value: ExportTypeType) => {
                dispatch(
                    patchMultipleOrdersFetch({
                        orders: ordersData?.data.filter((order) => selectedRows.includes(String(order.ID))),
                        data: { ALLOW_EXPEDITION: value }
                    })
                );
            }
        });
    };

    const handleMassChangeStatus = (statuses: ConstantsCodeType[]) => {
        setChangeStatusConfirmParams({
            title: translate('orders.action.changeStatuses'),
            content: translate('orders.action.changeStatusesText', { count: selectedRows.length }),
            pickCancelReasonContent: translate('orders.action.pickCancelReason'),
            options: statuses,
            optionsValue: statuses[0].value,
            showCancelReason: ordersData?.data.some((order) => selectedRows.includes(String(order.ID)) && CANCEL_REASON_ORDER_STATES.includes(order.STATUS!)),
            action: (value: ExportTypeType, cancelReason?: string | null) => {
                dispatch(
                    patchMultipleOrdersFetch({
                        orders: ordersData?.data.filter((order) => selectedRows.includes(String(order.ID))),
                        data: { STATUS: value, CANCEL_REASON: cancelReason }
                    })
                );
            }
        });
    };

    const handleAddRemoveProductDialog = () => {
        setShowAddProductDialog(true);
    };

    const handleAddProductsToOrders = (products: OrderProductType[], addProductsIfExists: boolean) => {
        const orders = ordersData?.data.filter((order) => selectedRows.includes(String(order.ID)));
        dispatch(
            addOrderProductsFetch({
                orderIds: ordersData?.data.filter((order) => selectedRows.includes(String(order.ID))).map((order) => order.ID),
                orderProducts: products,
                addProductsIfExists
            })
        );
        setSelectedRows([]);
    };

    const handleRemoveProductsFromOrders = (productIds: number[]) => {
        const orders = ordersData?.data.filter((order) => selectedRows.includes(String(order.ID)));
        dispatch(
            removeOrderProductsFetch({
                orderIds: ordersData?.data.filter((order) => selectedRows.includes(String(order.ID))).map((order) => order.ID),
                productIds
            })
        );
        setSelectedRows([]);
    };

    const handleAllProviders = (value: boolean) => {
        setSelectedRows([]);
        setAllProviders(value);
    };

    const tableAllColumns: TableColumnType[] = getOrderGridColumns(
        allProviders ? allColumnsWithProvider : allColumns,
        canSeeEgon
    );

    const handleCreateClaim = async (row: TableRowType) => {
        const claims = await getOrderClaims(row.ID);
        if (!claims.length) {
            navigate(`/orders/${row.ID}/claims/new/`);
            return;
        }

        setConfirmParams(
            buildClaimExistsDialogParams({
                orderId: row.ID,
                eshopOrderId: row.ESHOP_ORDER_ID || '',
                claims,
                canCreateMultiple: permissionCreateMultipleClaims,
                onConfirm: () => {
                    navigate(`/orders/${row.ID}/claims/new/`);
                },
            })
        );
    };

    const handleCreateReturn = async (row: TableRowType) => {
        const returns = await getOrderReturns(row.ID);
        if (!returns.length) {
            navigate(`/orders/${row.ID}/returns/new/`);
        } else {
            const rmaList = returns.map((ret: { RMA: string }) => ret.RMA).join('\n');
            setConfirmParams({
                title: translate('returnDetail.confirmReturnExists'),
                content: translate('returnDetail.confirmReturnExistsText', {
                    orderId: row.ID,
                    eshopOrderId: row.ESHOP_ORDER_ID || '',
                    rmaList
                }),
                action: () => {
                    navigate(`/orders/${row.ID}/returns/new/`);
                }
            });
        }
    };

    const tableRowActions: RowActionPropsType[] = [
        {
            icon: <OrderIcon />,
            label: translate('orders.action.orderDetail'),
            getLink: (row) => `/orders/${row.ID}/`,
            onClick: changeProvider,
            hiddenFce: () => !permissionOrderDetail
        },
        {
            icon: <ReturnIcon />,
            label: translate('orders.action.createReturn'),
            onClick: async (row) => {
                await changeProvider(row);
                await handleCreateReturn(row);
            },
            hiddenFce: (row) => !permissionReturnCreate || !CREATE_RETURN_STATUSES.includes(row.STATUS)
        },
        {
            icon: <ClaimIcon />,
            label: translate('orders.action.createClaim'),
            onClick: async (row) => {
                await changeProvider(row);
                await handleCreateClaim(row);
            },
            hiddenFce: (row) => !permissionClaimCreate || !CREATE_CLAIM_STATUSES.includes(row.STATUS)
        }
    ];
    const shipperServiceCodes = useMemo(
        () => buildShipperServiceOptions(serviceCodes, shipperCodes),
        [serviceCodes, shipperCodes]
    );
    const tableSelectOptions: {
        [key: string]: ConstantsCodeType[];
    } = {
        orderStatusCodes,
        shipperServiceCodes,
        paymentCodes,
        shipperCodes,
        boolNumberCodes,
        orderTypeCodes,
        currencyCodes,
        countryOptionsCodes,
        orderMarketplacesCodes,
        shippingBackendCodes
    };

    return (
        <>
            <TopBlock
                handleExport={handleExport}
                allProviders={allProviders}
                providerAllowedOrderProductsEdit={!!provider?.ALLOWED_ORDER_PRODUCTS_EDIT}
                permissionOrderUpdate={permissionOrderUpdate}
                permissionOrderDetail={permissionOrderDetail}
                permissionAllProviders={permissionAllProviders}
                permissionOrderCreate={permissionOrderCreate}
                permissionShippingCreate={permissionShippingCreate}
                permissionDCIXImport={permissionDCIXImport}
                setAllProviders={handleAllProviders}
                selectedRows={selectedRows}
                ordersDataData={ordersData?.data}
                orderStatusCodes={orderStatusCodes}
                orderStatusCodesObject={orderStatusCodesObject}
                reloadTableData={reloadTableData}
                handleDowlonadCn22={handleDowlonadCn22}
                handleReorderShipping={handleReorderShipping}
                handleSynchronizeDCIX={handleSynchronizeDCIX}
                handleMassPrintFiles={handleMassPrintFiles}
                handleMassAllowExpeditions={handleMassAllowExpeditions}
                handleMassChangeStatus={handleMassChangeStatus}
                totalCount={ordersData?.totalCount}
                handleAddRemoveProducts={handleAddRemoveProductDialog}
            />
            <div data-testid="orders-list">
            <Table
                allColumns={tableAllColumns}
                ref={tableRef}
                rowIdKey="ID"
                rows={formatedData}
                loading={loading || customLoading || loadingMultiple}
                columsLabelPrefix="orders.table"
                options={tableOptions}
                totalCount={ordersData?.totalCount}
                onChangeOptions={handleChangeOptions}
                rowActions={tableRowActions}
                selectOptions={tableSelectOptions}
                userSettingsName="showOrderTableColumns"
                userSettingsData={userSettingsData}
                editUserSettings={editUserSettings}
                optionsSessionName={SESSION_NAME}
                selectedRows={!allProviders ? selectedRows : undefined}
                setSelectedRows={!allProviders ? setSelectedRows : undefined}
                heightVariant="top-tab"
                customRender={(column, row) => customRender(column, row, orderStatusCodesObject)}
                fullHeight
                optionStyleFce={colorOptionFce}
            />
            </div>
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    options={confirmParams.options}
                    optionsValue={confirmParams.optionsValue}
                    action={confirmParams.action}
                    onClose={() => setConfirmParams(null)}
                    okLabel={confirmParams.okLabel}
                />
            )}
            {showAddRemoveProductDialog && (
                <AddRemoveProductDialog title={translate('orders.action.addRemoveProducts')}
                                        content={translate('orders.action.addRemoveProductsText', { count: selectedRows.length })}
                                        onClose={() => setShowAddProductDialog(false)}
                                        addAction={handleAddProductsToOrders}
                                        orderIds={selectedRows.map((id) => Number(id))}
                                        removeAction={handleRemoveProductsFromOrders}
                />
            )}
            {changeStatusConfirmParams && (
                <ChangeStatusDialog
                    title={changeStatusConfirmParams.title}
                    content={changeStatusConfirmParams.content}
                    pickCancelReasonContent={changeStatusConfirmParams.pickCancelReasonContent}
                    options={changeStatusConfirmParams.options}
                    optionsValue={changeStatusConfirmParams.optionsValue}
                    action={changeStatusConfirmParams.action}
                    onClose={() => setChangeStatusConfirmParams(null)}
                    showCancelReason={changeStatusConfirmParams.showCancelReason}
                    cancelReasonOptions={orderCancelReasonCodes} />
            )}
        </>
    );
};

export default AllOrdersContainer;
